import type { SupabaseClient } from "@supabase/supabase-js";
import { getLocationCoordinates } from "@/lib/locationData";
import type { Database, Json } from "@/lib/database.types";
import type { ResolvedLocationIds } from "@/lib/locationIds";
import { resolveSellListingLocationIds } from "@/lib/listing/listingLocationIds";
import type { DayOfWeek, ListingSource } from "@/lib/types";
import { LISTING_TITLE_MAX_LENGTH } from "@/lib/listing/chairListingSpec";
import { resolveCanonicalFanSubcategoryId } from "@/lib/listing/fanListingSpec";
import {
  isHiddenFromSellPickerByListingFamilyRegistry,
  resolveCanonicalSubcategoryIdFromRegistry,
} from "@/lib/listing/listingFamilyRegistry";
import { emptyFamilySpecs } from "@/lib/listing/listingFamilyRuntime";
import { resolveCanonicalGasCookerSubcategoryId } from "@/lib/listing/gasCookerListingSpec";
import { resolveCanonicalFridgeSubcategoryId } from "@/lib/listing/fridgeListingSpec";
import { resolveCanonicalKitchenCabinetSubcategoryId } from "@/lib/listing/kitchenCabinetListingSpec";
import { resolveCanonicalApplianceSubcategoryId } from "@/lib/listing/applianceInverterListingSpec";
import {
  buildListingAttributesForSubmit,
  getListingSpecAttributesEnforcementError,
} from "@/lib/listing/listingSpecPlugin";
import { composeTitle, getSubcategoryName } from "@/lib/listing/titleComposer";
import type { CreateSellListingFormPayload } from "@/lib/listing/createSellListing";
import { assertSellListingFreeText } from "@/lib/listing/sellListingContract";
import { resolveSellListingIdentityFields } from "@/lib/listing/listingIdentityPolicy";
import { resolveActiveCategoryIdsFromDatabase } from "@/lib/categories/activeCategoryIds";
import { LISTING_SPEC_VERSION } from "@/lib/listing/specRequirements";
import {
  buildListingGateShadowPersistFields,
  declaredCreatePhotoCountForHardGate,
} from "@/lib/listing-lifecycle/persistListingGateShadow";
import {
  isDeprecatedAudioSubcategory,
  isDeprecatedFurnitureSubcategory,
  isHiddenFromSellPickerApplianceSubcategory,
  isHiddenFromSellPickerFanSubcategory,
  isHiddenFromSellPickerGasCookerSubcategory,
  isHiddenFromSellPickerFridgeSubcategory,
  isHiddenFromSellPickerKitchenCabinetSubcategory,
  isHiddenFromSellPickerSnookerBoardSubcategory,
} from "@/lib/categoryStructure";

const LAGOS_LOCATIONS = getLocationCoordinates();

async function loadActiveSubcategorySet(): Promise<Set<string>> {
  if (typeof window === "undefined") {
    const { subcategories } = await resolveActiveCategoryIdsFromDatabase();
    return new Set(subcategories);
  }
  const activeRes = await fetch("/api/categories/active", { method: "GET" });
  if (!activeRes.ok) {
    throw new Error(
      "Could not verify category availability. Please try again in a moment.",
    );
  }
  const { subcategories: activeSubcategoryList } = (await activeRes.json()) as {
    subcategories: string[];
  };
  return new Set(activeSubcategoryList);
}

export type InsertSellListingCoreParams = {
  supabase: SupabaseClient<Database>;
  userId: string;
  itemId: string;
  formData: CreateSellListingFormPayload;
  pickupDays: DayOfWeek[];
  pickupStartHour: string;
  pickupEndHour: string;
  listingBatchId?: string | null;
  listingSource?: ListingSource;
  /** Structured ids from `HierarchicalLocationSelect` when available. */
  locationIds?: ResolvedLocationIds | null;
};

export type InsertSellListingCoreResult = {
  itemId: string;
  persistedTitle: string;
  composedTitle: string;
  numericPrice: number;
};

/**
 * Rolls back a partially created listing. Throws if the item row cannot be removed
 * (Supabase RLS can silently no-op deletes — we require a returned row).
 */
export async function removeSellListingItem(
  supabase: SupabaseClient<Database>,
  itemId: string,
): Promise<void> {
  const { error: pickupError } = await supabase
    .from("pickup_availability")
    .delete()
    .eq("item_id", itemId);
  if (pickupError) {
    throw new Error(
      `Could not clean up listing ${itemId} (${pickupError.message}). Please delete it from My Items or contact support.`,
    );
  }

  const { data: deletedRows, error: itemError } = await supabase
    .from("items")
    .delete()
    .eq("id", itemId)
    .select("id");

  if (itemError) {
    throw new Error(
      `Could not clean up listing ${itemId} (${itemError.message}). Please delete it from My Items or contact support.`,
    );
  }
  if (!deletedRows?.length) {
    throw new Error(
      `Could not clean up listing ${itemId}. Please delete it from My Items or contact support.`,
    );
  }
}

/**
 * Inserts item row + pickup availability (no images, analytics, or notifications).
 */
export async function insertSellListingCore(
  params: InsertSellListingCoreParams,
): Promise<InsertSellListingCoreResult> {
  const {
    supabase,
    userId,
    itemId,
    formData,
    pickupDays,
    pickupStartHour,
    pickupEndHour,
    listingBatchId = null,
    listingSource = "single",
    locationIds: pickerLocationIds = null,
  } = params;

  const freeTextCheck = assertSellListingFreeText(
    formData.title,
    formData.description,
    formData.defects,
  );
  if (!freeTextCheck.ok) {
    throw new Error(freeTextCheck.message);
  }

  const numericPrice = Number(formData.price.replace(/,/g, ""));
  const locationResolution = resolveSellListingLocationIds(
    formData.location,
    pickerLocationIds,
  );
  if (!locationResolution.ok) {
    throw new Error(locationResolution.error);
  }
  const locationIds = locationResolution.ids;

  const locationName = formData.location.split(",")[0].trim();
  let coordinates: { lat: number; lng: number } | null = null;
  if (LAGOS_LOCATIONS[locationName]) {
    coordinates = LAGOS_LOCATIONS[locationName];
  } else {
    for (const [key, coords] of Object.entries(LAGOS_LOCATIONS)) {
      if (locationName.includes(key) || key.includes(locationName)) {
        coordinates = coords;
        break;
      }
    }
  }

  const activeSubcategorySet = await loadActiveSubcategorySet();
  if (
    isDeprecatedFurnitureSubcategory(formData.subcategory) ||
    isDeprecatedAudioSubcategory(formData.subcategory) ||
    isHiddenFromSellPickerApplianceSubcategory(formData.subcategory) ||
    isHiddenFromSellPickerFanSubcategory(formData.subcategory) ||
    isHiddenFromSellPickerByListingFamilyRegistry(formData.subcategory) ||
    isHiddenFromSellPickerGasCookerSubcategory(formData.subcategory) ||
    isHiddenFromSellPickerFridgeSubcategory(formData.subcategory) ||
    isHiddenFromSellPickerKitchenCabinetSubcategory(formData.subcategory) ||
    isHiddenFromSellPickerSnookerBoardSubcategory(formData.subcategory) ||
    !activeSubcategorySet.has(formData.subcategory)
  ) {
    throw new Error(
      "This subcategory is not available for new listings at the moment.",
    );
  }

  const { brand: brandValue, model: modelValue } =
    resolveSellListingIdentityFields({
      brand: formData.brand,
      model: formData.model,
      category: formData.category,
    });
  const sizeText = formData.size?.trim() ?? "";
  const familySpecs = formData.familySpecs ?? emptyFamilySpecs();
  const subcategoryId =
    resolveCanonicalSubcategoryIdFromRegistry(formData.subcategory) ??
    resolveCanonicalFanSubcategoryId(formData.subcategory) ??
    resolveCanonicalGasCookerSubcategoryId(formData.subcategory) ??
    resolveCanonicalFridgeSubcategoryId(formData.subcategory) ??
    resolveCanonicalKitchenCabinetSubcategoryId(formData.subcategory) ??
    resolveCanonicalApplianceSubcategoryId(formData.subcategory) ??
    formData.subcategory;
  const attributesJson = buildListingAttributesForSubmit({
    subcategoryId,
    sizeText,
    familySpecs,
  });
  const specError = getListingSpecAttributesEnforcementError({
    subcategoryId,
    attributes: attributesJson,
    activeSubcategoryIds: activeSubcategorySet,
  });
  if (specError) {
    throw new Error(specError);
  }

  const composedListingTitle = composeTitle({
    size: sizeText || undefined,
    brand: brandValue || undefined,
    model: modelValue || undefined,
    subcategoryId,
    subcategoryName: getSubcategoryName(subcategoryId) ?? undefined,
    familySpecs,
  }).trim();
  const persistedTitle = (composedListingTitle || formData.title.trim())
    .trim()
    .slice(0, LISTING_TITLE_MAX_LENGTH);

  const nowIso = new Date().toISOString();
  const { fields: gateShadowFields, result: gateShadow } =
    buildListingGateShadowPersistFields({
      photoCount: declaredCreatePhotoCountForHardGate(),
      subcategoryId,
      attributes: attributesJson,
      priceSuitability: null,
      title: persistedTitle,
      description: formData.description.trim(),
      defects: formData.defects.trim(),
      nowIso,
    });
  // Shadow only — never changes status. Price band filled on admin queue / later refresh.
  if (typeof console !== "undefined") {
    console.info("[listing-lifecycle] hard-gate shadow on create", {
      itemId,
      pass: gateShadow.pass,
      pricePending: gateShadow.pricePending,
      score: gateShadowFields.listing_quality_score,
    });
  }

  const { error: itemError } = await supabase
    .from("items")
    .insert({
      id: itemId,
      title: persistedTitle,
      description: formData.description.trim(),
      price: numericPrice,
      defects: formData.defects.trim(),
      category: formData.category,
      subcategory: subcategoryId,
      location: formData.location,
      location_state_id: locationIds.locationStateId,
      location_lga_id: locationIds.locationLgaId,
      location_neighborhood_id: locationIds.locationNeighborhoodId,
      coordinates,
      seller_id: userId,
      status: "new_submission",
      quantity: formData.quantity,
      brand: brandValue ? brandValue : null,
      model: modelValue ? modelValue : null,
      attributes: attributesJson as unknown as Json,
      spec_version: LISTING_SPEC_VERSION,
      listing_batch_id: listingBatchId,
      listing_source: listingSource,
      listing_gate_breakdown: gateShadowFields.listing_gate_breakdown,
      listing_quality_score: gateShadowFields.listing_quality_score,
      created_at: nowIso,
      updated_at: nowIso,
      status_entered_at: nowIso,
    })
    .select()
    .single();

  if (itemError) {
    console.error("Error creating item:", itemError);
    throw new Error(itemError.message);
  }

  if (pickupDays.length > 0) {
    const { error: pickupError } = await supabase
      .from("pickup_availability")
      .insert(
        pickupDays.map((day) => ({
          item_id: itemId,
          day_of_week: day,
          start_hour: parseInt(pickupStartHour, 10),
          end_hour: parseInt(pickupEndHour, 10),
          created_at: new Date().toISOString(),
        })),
      );

    if (pickupError) {
      console.error("Error creating pickup availability:", pickupError);
      await removeSellListingItem(supabase, itemId);
      throw new Error(pickupError.message);
    }
  }

  return {
    itemId,
    persistedTitle,
    composedTitle: composedListingTitle || formData.title.trim(),
    numericPrice,
  };
}
